import {Injectable} from '@angular/core';
import {ProviderId, SaleReport, SaleReportType} from '@emma-labs/shared/typescript';
import {first, Observable, switchMap} from 'rxjs';
import {map} from 'rxjs/operators';
import {SaleReport as SaleReportFrontendified} from '../../entities/page-shared-object/sale-report';
import {SaleReportPageObject} from '../../entities/sale-report-page-object/sale-report-page-object';
import {SaleReportService} from '../sale/sale-report.service';

@Injectable({
  providedIn: 'root',
})
export class SaleReportPageOrchestrator {
  constructor(private readonly saleReportService: SaleReportService) {}

  // We first get the sale report by id, then we get the associated sale reports using the sale report proposal id
  get(saleReportId: string): Observable<SaleReportPageObject> {
    return this.saleReportService.search(saleReportId).pipe(
      first(),
      switchMap((saleReports) => {
        const currentSaleReport = saleReports.find((saleReport) => saleReport.id === saleReportId);
        if (!currentSaleReport) {
          throw new Error(`Sale report with id ${saleReportId} not found`);
        }

        return this.saleReportService
          .search(currentSaleReport.proposal.id!)
          .pipe(map((saleReports) => this.buildSaleReportPageObject(saleReportId, saleReports)));
      }),
    );
  }

  private buildSaleReportPageObject(saleReportId: string, saleReports: SaleReport[]): SaleReportPageObject {
    const currentSaleReport = saleReports.find((saleReport) => saleReport.id === saleReportId);
    if (!currentSaleReport) {
      throw new Error(`Sale report with id ${saleReportId} not found`);
    }

    const associatedSaleReports = saleReports.filter((saleReport) => saleReport.id !== currentSaleReport.id);

    return {
      currentSaleReport: this.getSaleReportDetails(currentSaleReport),
      associatedSaleReports: associatedSaleReports.map((saleReport) => this.getSaleReportDetails(saleReport)),
    };
  }

  private getSaleReportDetails(saleReport: SaleReport): SaleReportFrontendified {
    const srFrontendified: SaleReportFrontendified = {
      id: saleReport.id,
      insurer: saleReport.insurer,
      insurances: saleReport.protection.personInsurances.map((i) => ({
        annualPremium: i.annualPrice,
        coverage: i.coverage,
        duration: i.duration,
        insuranceType: i.type,
      })),
      protection: saleReport.protection,
      fullName: `${saleReport.proposal.insuree.firstName} ${saleReport.proposal.insuree.lastName}`,
      policyNumber: saleReport.policyNumber,
      status: saleReport.status,
      type: saleReport.type,
      agent: saleReport.proposal.agents.find((agent) => agent.isPrimary)?.email ?? 'Self-Serve',
      hasReplacementNotice: saleReport.proposal.hasPolicyReplacementNotice,
      canDownloadContract: saleReport.contractReceived,
      canAccessProposal: saleReport.proposal.providerId !== ProviderId.HUMANIA_EMMA, // We do not support the proposal page for sales that happened in the old systems (monolith)
      secretCode: saleReport.secretCode,
    };

    if (saleReport.proposal.id && saleReport.type !== SaleReportType.EXTERNAL) {
      srFrontendified.proposalLink = `dashboard/archived-proposal/${btoa(saleReport.proposal.id)}`;
    }

    return srFrontendified;
  }
}
